import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Camera, ChevronLeft, ChevronRight, X, Check, Search, Upload, Eye } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { SignedImage } from '../common/SignedFile';

interface TruckPhotoWizardProps {
  existingData?: any | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface PhotoCategory {
  id: string;
  label: string;
  description: string;
  fieldName: keyof PhotoData;
}

interface PhotoData {
  pictures_4_sides: File[];
  dashboard_picture: File[];
  lights: File[];
  tires: File[];
  oil_level: File[];
  coolant_tank: File[];
  windshield: File[];
  visible_damage: File[];
  fire_triangles: File[];
  three_in_one: File[];
}

export function TruckPhotoWizard({ existingData, onClose, onSuccess }: TruckPhotoWizardProps) {
  const { user, profile } = useAuth();
  const { showToast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form data - initialize with existing data if editing
  const [inspectionId] = useState(existingData?.id || null);
  const [truckNumber, setTruckNumber] = useState(existingData?.truck_number || '');
  const [truckSearchQuery, setTruckSearchQuery] = useState(existingData?.truck_number || '');
  const [trucks, setTrucks] = useState<any[]>([]);
  const [showTruckDropdown, setShowTruckDropdown] = useState(false);
  const [outIn, setOutIn] = useState<'OUT' | 'IN'>(existingData?.out_in || 'OUT');
  const [driverName, setDriverName] = useState(existingData?.driver_name || '');
  const [driverSearchQuery, setDriverSearchQuery] = useState(existingData?.driver_name || '');
  const [drivers, setDrivers] = useState<any[]>([]);
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [company, setCompany] = useState(existingData?.company || '');
  const [companies, setCompanies] = useState<any[]>([]);
  const [inspectionDate, setInspectionDate] = useState(existingData?.inspection_date || new Date().toISOString().split('T')[0]);
  const [yardLetterPhotos, setYardLetterPhotos] = useState<File[]>([]);
  const [existingYardLetterUrl] = useState(existingData?.yard_letter || null);
  const [currentPhotoCategory, setCurrentPhotoCategory] = useState(0);
  const yardLetterInputRef = useRef<HTMLInputElement>(null);

  // Photo data
  const [photoData, setPhotoData] = useState<PhotoData>({
    pictures_4_sides: [],
    dashboard_picture: [],
    lights: [],
    tires: [],
    oil_level: [],
    coolant_tank: [],
    windshield: [],
    visible_damage: [],
    fire_triangles: [],
    three_in_one: [],
  });

  const photoCategories: PhotoCategory[] = [
    { id: '4-sides', label: 'Pictures of All 4 Sides', description: 'Take photos of front, back, left side, and right side of the truck', fieldName: 'pictures_4_sides' },
    { id: 'dashboard', label: 'Dashboard Picture', description: 'Take a clear photo of the dashboard showing all gauges', fieldName: 'dashboard_picture' },
    { id: 'lights', label: 'Lights', description: 'Take photos of all exterior lights (headlights, taillights, turn signals)', fieldName: 'lights' },
    { id: 'tires', label: 'Tires', description: 'Take photos of all tires showing tread depth and condition', fieldName: 'tires' },
    { id: 'oil', label: 'Oil Level (Dipstick)', description: 'Take a photo of the oil dipstick showing the oil level', fieldName: 'oil_level' },
    { id: 'coolant', label: 'Coolant Tank', description: 'Take a photo of the coolant reservoir showing the fluid level', fieldName: 'coolant_tank' },
    { id: 'windshield', label: 'Windshield', description: 'Take a photo of the windshield (inside and outside)', fieldName: 'windshield' },
    { id: 'damage', label: 'Visible Damage', description: 'Take photos of any visible damage or issues (if none, skip)', fieldName: 'visible_damage' },
    { id: 'fire', label: 'Fire Extinguisher & Triangles', description: 'Take photos of fire extinguisher and safety triangles', fieldName: 'fire_triangles' },
    { id: '3in1', label: '3 in 1', description: 'Take photos of the 3-in-1 kit', fieldName: 'three_in_one' },
  ];

  const personName = profile?.full_name || user?.email?.split('@')[0] || 'User';

  // Load trucks and drivers for search
  useEffect(() => {
    loadTrucks();
    loadDrivers();
    loadCompanies();
  }, []);

  const loadTrucks = async () => {
    try {
      const { data, error } = await supabase
        .from('trucks')
        .select('id, unit_number, make, model, status')
        .order('unit_number');

      if (error) throw error;
      setTrucks(data || []);
    } catch (error: any) {
      console.error('Error loading trucks:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, code')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setCompanies(data || []);
    } catch (error: any) {
      console.error('Error loading companies:', error);
    }
  };

  const loadDrivers = async () => {
    try {
      const { data, error } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, phone_number, companies(code)')
        .order('first_name');

      if (error) throw error;
      setDrivers(data || []);
    } catch (error: any) {
      console.error('Error loading drivers:', error);
    }
  };

  const filteredTrucks = trucks.filter(truck =>
    truck.unit_number.toLowerCase().includes(truckSearchQuery.toLowerCase())
  );

  const filteredDrivers = drivers.filter(driver => {
    const fullName = `${driver.first_name} ${driver.last_name}`.toLowerCase();
    return fullName.includes(driverSearchQuery.toLowerCase());
  });

  const handleTruckSelect = (truck: any) => {
    setTruckNumber(truck.unit_number);
    setTruckSearchQuery(truck.unit_number);
    setShowTruckDropdown(false);
  };

  const handleDriverSelect = (driver: any) => {
    const fullName = `${driver.first_name} ${driver.last_name}`;
    setDriverName(fullName);
    setDriverSearchQuery(fullName);
    setShowDriverDropdown(false);
    
    // Auto-populate company if driver has one
    if (driver.companies?.code) {
      setCompany(driver.companies.code);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const category = photoCategories[currentPhotoCategory];
    setPhotoData(prev => ({
      ...prev,
      [category.fieldName]: [...prev[category.fieldName], ...files]
    }));
  };

  const handleCameraCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removePhoto = (categoryField: keyof PhotoData, index: number) => {
    setPhotoData(prev => ({
      ...prev,
      [categoryField]: prev[categoryField].filter((_, i) => i !== index)
    }));
  };

  const uploadPhotosToStorage = async (files: File[], category: string): Promise<string[]> => {
    const uploadedUrls: string[] = [];

    for (const file of files) {
      // Convert image to JPEG if it's in an unsupported format
      let fileToUpload = file;
      const fileType = file.type;
      
      // Check if format is unsupported (AVIF, HEIC, etc.)
      if (fileType === 'image/avif' || fileType === 'image/heic' || fileType === 'image/heif') {
        // Convert to JPEG
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = URL.createObjectURL(file);
        });
        
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.9);
        });
        
        fileToUpload = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), { type: 'image/jpeg' });
      }
      
      const fileName = `truck-${truckNumber}-${category}-${Date.now()}-${Math.random().toString(36).substring(7)}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(fileName, fileToUpload, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(fileName);

      uploadedUrls.push(publicUrl);
    }

    return uploadedUrls;
  };

  const handleSubmit = async () => {
    try {
      setUploading(true);

      // Upload all photos to storage
      const uploadedData: any = {};
      
      for (const category of photoCategories) {
        const files = photoData[category.fieldName];
        if (files.length > 0) {
          // Upload new photos
          const newUrls = await uploadPhotosToStorage(files, category.id);
          // Merge with existing photos if editing
          const existingUrls = existingData?.[category.fieldName] || [];
          uploadedData[category.fieldName] = [...existingUrls, ...newUrls];
        } else if (existingData?.[category.fieldName]) {
          // Keep existing photos if no new ones uploaded
          uploadedData[category.fieldName] = existingData[category.fieldName];
        } else {
          uploadedData[category.fieldName] = [];
        }
      }

      // Upload yard letter photos
      let yardLetterUrl = existingYardLetterUrl;
      if (yardLetterPhotos.length > 0) {
        const yardLetterUrls = await uploadPhotosToStorage(yardLetterPhotos, 'yard-letter');
        yardLetterUrl = yardLetterUrls[0];
      }

      const dataToSave = {
        truck_number: truckNumber,
        truck_unit_number: truckNumber,
        person: personName,
        out_in: outIn,
        driver_name: driverName || null,
        company: company || null,
        inspection_date: inspectionDate,
        yard_letter: yardLetterUrl || null,
        ...uploadedData,
        updated_by: user?.email,
      };

      let error;
      if (inspectionId) {
        // Update existing record
        const result = await supabase
          .from('truck_photos')
          .update(dataToSave)
          .eq('id', inspectionId);
        error = result.error;
      } else {
        // Insert new record
        const result = await supabase
          .from('truck_photos')
          .insert({
            ...dataToSave,
            created_by: user?.email,
          });
        error = result.error;
      }

      if (error) throw error;

      showToast('success', inspectionId ? 'Truck inspection updated successfully!' : 'Truck photos submitted successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error submitting truck photos:', error);
      showToast('error', error.message || 'Failed to submit truck photos');
    } finally {
      setUploading(false);
    }
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return truckNumber.trim() !== '';
      case 2:
        return true;
      case 3:
        return true;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const totalSteps = 4;
  const progress = (currentStep / totalSteps) * 100;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Select Truck</h2>
              <p className="text-slate-600">Search and select the truck you're inspecting</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Inspector
              </label>
              <div className="px-4 py-3 bg-slate-100 rounded-lg text-slate-900 font-medium">
                {personName}
              </div>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Truck Number *
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={truckSearchQuery}
                  onChange={(e) => {
                    setTruckSearchQuery(e.target.value);
                    setShowTruckDropdown(true);
                  }}
                  onFocus={() => setShowTruckDropdown(true)}
                  placeholder="Search truck number..."
                  className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                />
              </div>

              {showTruckDropdown && filteredTrucks.length > 0 && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredTrucks.map((truck) => (
                    <button
                      key={truck.id}
                      onClick={() => handleTruckSelect(truck)}
                      className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                    >
                      <div className="font-semibold text-slate-900">{truck.unit_number}</div>
                      <div className="text-sm text-slate-600">{truck.make} {truck.model}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Status *
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setOutIn('OUT')}
                  className={`py-4 px-6 rounded-lg font-semibold text-lg transition-all ${
                    outIn === 'OUT'
                      ? 'bg-orange-600 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  OUT
                </button>
                <button
                  onClick={() => setOutIn('IN')}
                  className={`py-4 px-6 rounded-lg font-semibold text-lg transition-all ${
                    outIn === 'IN'
                      ? 'bg-green-600 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  IN
                </button>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Additional Information</h2>
              <p className="text-slate-600">Fill in the details (optional)</p>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Driver Name
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={driverSearchQuery}
                  onChange={(e) => {
                    setDriverSearchQuery(e.target.value);
                    setShowDriverDropdown(true);
                  }}
                  onFocus={() => setShowDriverDropdown(true)}
                  placeholder="Search driver name..."
                  className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                />
              </div>

              {/* Driver Dropdown */}
              {showDriverDropdown && filteredDrivers.length > 0 && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredDrivers.map((driver) => (
                    <button
                      key={driver.id}
                      onClick={() => handleDriverSelect(driver)}
                      className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                    >
                      <div className="font-semibold text-slate-900">{driver.first_name} {driver.last_name}</div>
                      {driver.phone_number && (
                        <div className="text-sm text-slate-600">{driver.phone_number}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Company
              </label>
              <div className="grid grid-cols-1 gap-3">
                {companies.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setCompany(c.code)}
                    className={`py-4 px-6 rounded-lg font-semibold text-lg transition-all ${
                      company === c.code
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Inspection Date
              </label>
              <input
                type="date"
                value={inspectionDate}
                onChange={(e) => setInspectionDate(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Yard Letter (Photo)
              </label>
              
              {/* Photo Preview */}
              {(yardLetterPhotos.length > 0 || existingYardLetterUrl) && (
                <div className="mb-3 grid grid-cols-2 gap-3">
                  {existingYardLetterUrl && yardLetterPhotos.length === 0 && (
                    <div className="relative aspect-square rounded-lg overflow-hidden bg-slate-100">
                      <SignedImage
                        stored={existingYardLetterUrl}
                        alt="Existing Yard Letter"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 left-2 px-2 py-1 bg-blue-600 text-white text-xs rounded">
                        Existing
                      </div>
                    </div>
                  )}
                  {yardLetterPhotos.map((file, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Yard Letter ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => setYardLetterPhotos(prev => prev.filter((_, i) => i !== index))}
                        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Camera Input */}
              <input
                ref={yardLetterInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 0) {
                    setYardLetterPhotos(prev => [...prev, ...files]);
                  }
                }}
                className="hidden"
              />

              <button
                onClick={() => yardLetterInputRef.current?.click()}
                className="w-full py-4 bg-slate-600 text-white rounded-lg font-semibold text-lg flex items-center justify-center gap-3 hover:bg-slate-700 active:bg-slate-800 transition-colors"
              >
                <Camera className="w-6 h-6" />
                {yardLetterPhotos.length > 0 ? 'Add More Photos' : 'Take Photo of Yard Letter'}
              </button>
              
              {yardLetterPhotos.length > 0 && (
                <p className="text-center text-sm text-slate-500 mt-2">
                  {yardLetterPhotos.length} photo{yardLetterPhotos.length !== 1 ? 's' : ''} added
                </p>
              )}
            </div>
          </div>
        );

      case 3:
        const category = photoCategories[currentPhotoCategory];
        const categoryPhotos = photoData[category.fieldName];

        return (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-bold text-slate-900">{category.label}</h2>
                <span className="text-sm text-slate-600">
                  {currentPhotoCategory + 1} of {photoCategories.length}
                </span>
              </div>
              <p className="text-slate-600">{category.description}</p>
            </div>

            {(categoryPhotos.length > 0 || (existingData?.[category.fieldName] && existingData[category.fieldName].length > 0)) && (
              <div className="grid grid-cols-2 gap-3">
                {/* Show existing photos */}
                {existingData?.[category.fieldName]?.map((url: string, index: number) => (
                  <div key={`existing-${index}`} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100">
                    <SignedImage
                      stored={url}
                      alt={`Existing ${category.label} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 px-2 py-1 bg-blue-600 text-white text-xs rounded">
                      Existing
                    </div>
                  </div>
                ))}
                {/* Show new photos */}
                {categoryPhotos.map((file, index) => (
                  <div key={`new-${index}`} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`${category.label} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removePhoto(category.fieldName, index)}
                      className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />

            <button
              onClick={handleCameraCapture}
              className="w-full py-6 bg-blue-600 text-white rounded-lg font-semibold text-lg flex items-center justify-center gap-3 hover:bg-blue-700 active:bg-blue-800 transition-colors"
            >
              <Camera className="w-6 h-6" />
              Take Photo{categoryPhotos.length > 0 ? 's' : ''}
            </button>

            <div className="flex gap-3">
              {currentPhotoCategory > 0 && (
                <button
                  onClick={() => setCurrentPhotoCategory(prev => prev - 1)}
                  className="flex-1 py-3 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300"
                >
                  Previous Category
                </button>
              )}
              <button
                onClick={() => {
                  if (currentPhotoCategory < photoCategories.length - 1) {
                    setCurrentPhotoCategory(prev => prev + 1);
                  } else {
                    setCurrentStep(4);
                  }
                }}
                className="flex-1 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
              >
                {currentPhotoCategory < photoCategories.length - 1 ? 'Next Category' : 'Review & Submit'}
              </button>
            </div>

            <p className="text-center text-sm text-slate-500">
              {categoryPhotos.length} photo{categoryPhotos.length !== 1 ? 's' : ''} added
            </p>
          </div>
        );

      case 4:
        const totalPhotos = Object.values(photoData).reduce((sum, files) => sum + files.length, 0) + yardLetterPhotos.length;

        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Review & Submit</h2>
              <p className="text-slate-600">Review your inspection before submitting</p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Truck Number:</span>
                <span className="font-semibold text-slate-900">{truckNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Inspector:</span>
                <span className="font-semibold text-slate-900">{personName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Status:</span>
                <span className={`font-semibold ${outIn === 'OUT' ? 'text-orange-600' : 'text-green-600'}`}>
                  {outIn}
                </span>
              </div>
              {driverName && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Driver:</span>
                  <span className="font-semibold text-slate-900">{driverName}</span>
                </div>
              )}
              {company && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Company:</span>
                  <span className="font-semibold text-slate-900">{company}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-600">Date:</span>
                <span className="font-semibold text-slate-900">{new Date(inspectionDate).toLocaleDateString()}</span>
              </div>
              {yardLetterPhotos.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Yard Letter Photos:</span>
                  <span className="font-semibold text-slate-900">{yardLetterPhotos.length}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-600">Total Photos:</span>
                <span className="font-semibold text-slate-900">{totalPhotos}</span>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900">Photos by Category:</h3>
              {photoCategories.map((cat) => {
                const photos = photoData[cat.fieldName];
                if (photos.length === 0) return null;
                
                return (
                  <div key={cat.id} className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-700">{cat.label}</span>
                    <span className="text-sm font-medium text-blue-600">{photos.length} photo{photos.length !== 1 ? 's' : ''}</span>
                  </div>
                );
              })}
            </div>

            <button
              onClick={handleSubmit}
              disabled={uploading}
              className="w-full py-4 bg-green-600 text-white rounded-lg font-semibold text-lg flex items-center justify-center gap-3 hover:bg-green-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Submit Inspection
                </>
              )}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h1 className="text-xl font-bold text-slate-900">Truck Inspection</h1>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-4 pt-4">
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-sm text-slate-600 mt-2 text-center">
            Step {currentStep} of {totalSteps}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderStep()}
        </div>

        {/* Footer Navigation */}
        {currentStep !== 3 && currentStep !== 4 && (
          <div className="p-4 border-t border-slate-200 flex gap-3">
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="flex-1 py-3 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300 flex items-center justify-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
            )}
            <button
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!canProceedToNextStep()}
              className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              Next
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
